/**
 * StudentLayout.tsx  -  Child-friendly shell for the student portal.
 * Big buttons, bright colours, minimal text, emoji-heavy.
 */

import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  Home,
  Gamepad2,
  BookOpenCheck,
  Star,
  BookOpen,
  Map,
  ALargeSmall,
  LogOut,
  ShoppingBag,
  Sofa,
} from "lucide-react";
import { useStudentAuthStore } from "@/store/studentAuthStore";
import { SkipLink } from "@/components/SkipLink";

const NAV = [
  { to: "/student", icon: Home, label: "Home", exact: true },
  { to: "/student/homework", icon: BookOpenCheck, label: "Homework" },
  { to: "/student/letters", icon: ALargeSmall, label: "Letters" },
  { to: "/student/games", icon: Gamepad2, label: "Games" },
  { to: "/student/quest", icon: Map, label: "Quest" },
  { to: "/student/shop", icon: ShoppingBag, label: "Shop" },
  { to: "/student/room", icon: Sofa, label: "My Room" },
  { to: "/student/stars", icon: Star, label: "Stars" },
];

export default function StudentLayout() {
  const { student, clearStudent } = useStudentAuthStore();
  const navigate = useNavigate();

  function handleLogout() {
    clearStudent();
    navigate("/student/login");
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gradient-to-b from-purple-50 to-orange-50">
      <SkipLink />
      {/* ── Top bar ── */}
      <header className="flex h-16 items-center justify-between bg-purple-700 px-4 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-400 text-xl font-black text-purple-900">
            {student?.displayName?.[0]?.toUpperCase() ?? "?"}
          </div>
          <div>
            <p className="text-sm font-bold text-white leading-tight">
              {student?.displayName ?? "Student"}
            </p>
            <p className="text-xs text-purple-200">
              @{student?.username ?? ""}
            </p>
          </div>
        </div>

        {/* XP + coins + streak */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-full bg-purple-600 px-3 py-1.5">
            <span className="text-base">⭐</span>
            <span className="text-sm font-bold text-white">
              {student?.totalXp ?? 0} XP
            </span>
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-yellow-500 px-3 py-1.5">
            <span className="text-base">🪙</span>
            <span className="text-sm font-bold text-white">
              {student?.coins ?? 0}
            </span>
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-orange-500 px-3 py-1.5">
            <span className="text-base">🔥</span>
            <span className="text-sm font-bold text-white">
              {student?.streak ?? 0}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="rounded-full p-2 text-purple-200 hover:bg-purple-600"
            aria-label="Log out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* ── Content ── */}
      <main id="main-content" className="flex-1 overflow-y-auto p-4">
        <Outlet />
      </main>

      {/* ── Bottom nav (mobile-first) ── */}
      <nav
        aria-label="Student navigation"
        className="flex h-16 items-stretch border-t-2 border-purple-200 bg-white"
      >
        {NAV.map(({ to, icon: Icon, label, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center justify-center gap-0.5 transition-colors
               ${
                 isActive
                   ? "border-t-4 border-purple-600 bg-purple-50 text-purple-700"
                   : "text-gray-400 hover:text-purple-600"
               }`
            }
          >
            <Icon className="h-5 w-5" />
            <span className="text-[10px] font-semibold">{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
